import LoginLink from '@components/LoginLink';
import { APP_URL, USE_BLS_THEME } from '@constants';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { resolveUrl } from '~/utils/urlUtils';

import { useValidateRegistrationCodeMutation } from '../queries/useValidateRegistrationCodeMutation';
import { formatRegistrationCode, isValidRegistrationCode, sanitizeRegistrationCode } from '../utils/registrationCodeUtils';

export default function RegistrationCodeForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { registrationCode } = useRegisterContext();
  const [registrationCodeInput, setRegistrationCodeInput] = useState(registrationCode || '');

  const [error, setError] = useState('');

  const validateRegistrationCodeMutation = useValidateRegistrationCodeMutation({
    onSuccess: () => {
      void navigate('/auth');
    },
    onError: ({ commonErrorCode, isExpired }) => {
      if (commonErrorCode) {
        setError(commonErrorCode);
      } else {
        setError(`registration.errors.${isExpired ? 'expiredCode' : 'invalidCode'}`);
      }
    },
  });

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      validateRegistrationCodeMutation.mutate(registrationCodeInput);
    },
    [registrationCodeInput, validateRegistrationCodeMutation],
  );

  const onTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = sanitizeRegistrationCode(e.target.value);
    setRegistrationCodeInput(value || '');
  }, []);

  return (
    <form onSubmit={onSubmit}>
      <Stack sx={{ gap: 3 }}>
        <Stack sx={{ gap: 1, textAlign: 'center' }}>
          <Typography variant="h5">
            <Trans i18nKey="registration.title" />
          </Typography>
          <Typography>
            <Trans i18nKey="registration.message" />
          </Typography>
        </Stack>
        {error && (
          <Typography color="error">
            <Trans i18nKey={error} />
          </Typography>
        )}
        <Stack sx={{ gap: 4, width: '100%' }}>
          <TextField
            required
            autoFocus
            label={t('registration.employerCodeLabel')}
            onChange={onTextChange}
            value={formatRegistrationCode(registrationCodeInput)}
            helperText={t(USE_BLS_THEME ? 'registration.employerCodeHelpBLS' : 'registration.employerCodeHelpProbonio')}
            data-test-id="company-code"
          />
          <Stack sx={{ gap: 2, width: '100%' }}>
            <Button
              type="submit"
              loading={validateRegistrationCodeMutation.isPending}
              disabled={!isValidRegistrationCode(registrationCodeInput)}
            >
              <Trans i18nKey="common.actions.next" />
            </Button>
            {USE_BLS_THEME ? (
              <Button variant="outlined" href={resolveUrl(APP_URL)} role="link">
                <Trans i18nKey="common.actions.back" />
              </Button>
            ) : (
              <LoginLink />
            )}
          </Stack>
        </Stack>
      </Stack>
    </form>
  );
}
